import type { Expedition } from '../types';

export const translateExpeditions = (expeditions: Expedition[], currentLanguage: string): Expedition[] => {
	if (currentLanguage === 'en') return expeditions;

	const translations: Record<string, Record<string, Partial<Expedition>>> = {
  "kruger-experience-big-5": {
    "it": {
      "title": "Esperienza Kruger – Safari Big 5",
      "subtitle": "Iconico tracciamento dei Big 5 nella leggendaria riserva naturale del Sudafrica",
      "location": "Parco Nazionale Kruger",
      "country": "Sudafrica",
      "durationText": "Consigliata: 5 – 10 Giorni",
      "difficulty": "Relaxed",
      "description": "Immergiti nel rinomato Kruger National Park in Sudafrica. Incontra i leggendari Big 5 (leone, leopardo, rinoceronte, elefante e bufalo) nel loro habitat naturale, con emozionanti safari su 4x4 aperte all'alba e al crepuscolo con ranger esperti e tracker locali.",
      "highlights": [
        "Safari 4x4 aperti al sorgere del sole e al crepuscolo alla ricerca dei Big 5",
        "Ranger esperti e tracker certificati della fauna selvatica",
        "Cena tradizionale in boma sotto il cielo stellato africano",
        "Passeggiate esclusive nel bush alla scoperta della flora indigena e dei segreti delle tracce"
      ],
      "accommodations": "Ogni dettaglio è scelto per la massima integrazione negli ecosistemi circostanti, preservando un lusso di altissimo livello. Piscine private, terrazze panoramiche con vista mozzafiato.",
      "inclusions": [
        "Tutti i safari in veicoli 4x4 aperti con team professionale di ranger e tracker",
        "Soggiorno in lodge di lusso con cucina gourmet e bevande premium",
        "Tasse di conservazione e biglietti di ingresso al Parco Nazionale Kruger",
        "Trasferimenti panoramici tra l'aeroporto e il lodge di lusso"
      ],
      "requiredPreparations": [
        "Si prega di notare che il Parco Nazionale Kruger si trova in una zona a rischio malaria. Si raccomandano precauzioni/profilassi antimalarica e consulto medico prima della partenza."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Arrivo e Benvenuto al Crepuscolo nella Savana",
          "description": "Arrivo alla pista di atterraggio della concessione privata e accoglienza da parte del rappresentante del lodge. Sistemazione nella suite di lusso prima del primo safari pomeridiano nella savana dorata.",
          "activities": [
            "Ricevimento di Benvenuto",
            "Safari al Crepuscolo",
            "Cena di Benvenuto in Boma"
          ]
        },
        {
          "day": 2,
          "title": "Tracciamento all'Alba e Incontri con i Grandi Predatori",
          "description": "Sveglia prima dell'alba con il profumo di caffè fresco africano. Partenza mentre i predatori sono attivi, seguendo le impronte di leoni e leopardi lungo i letti dei fiumi in secca.",
          "activities": [
            "Safari all'Alba",
            "Caffè nella Savana",
            "Avvistamento Pomeridiano"
          ]
        },
        {
          "day": 3,
          "title": "Safari Nella Savana Profonda e Confluenze dei Fiumi",
          "description": "Attraversa habitat variegati, dalle fitte foreste di mopane alle praterie aperte. Osserva branchi di elefanti abbeverarsi e fare il bagno nelle pozze d'acqua.",
          "activities": [
            "Safari Giornalieri in 4x4",
            "Sosta Fotografica alla Pozza",
            "Cocktail al Tramonto nella Riserva"
          ]
        },
        {
          "day": 4,
          "title": "Safari a Piedi Guidato e Banchetto in Boma",
          "description": "Cammina a piedi con un ranger armato per interpretare i segreti della natura africana: impronte, piante medicinali e richiami degli uccelli.",
          "activities": [
            "Passeggiata Guidata nel Bush",
            "Relax in Piscina Privata",
            "Banchetto Gourmet al Chiaro di Luna"
          ]
        },
        {
          "day": 5,
          "title": "Ultimo Safari all'Alba e Saluti",
          "description": "Goditi un ultimo safari alle prime luci del mattino prima di una ricca colazione di arrivederci e del trasferimento programmato.",
          "activities": [
            "Safari all'Alba",
            "Brunch di Arrivederci al Lodge",
            "Trasferimento in Aeroporto"
          ]
        }
      ]
    },
    "fr": {
      "title": "Expérience Kruger – Safari Big 5",
      "subtitle": "Sur la piste des légendaires Big 5 dans le sanctuaire emblématique d'Afrique du Sud",
      "location": "Parc National Kruger",
      "country": "Afrique du Sud",
      "durationText": "Recommandée : 5 – 10 Jours",
      "difficulty": "Relaxed",
      "description": "Plongez au cœur du célèbre parc national Kruger en Afrique du Sud. Partez à la rencontre des mythiques Big 5 (lion, léopard, rhinocéros, éléphant et buffle) dans leur habitat naturel avec des safaris en 4x4 ouverts à l'aube et au crépuscule.",
      "highlights": [
        "Safaris 4x4 ouverts au lever et au coucher du soleil à la recherche des Big 5",
        "Rangers certifiés et pisteurs experts de la faune sauvage",
        "Dîner traditionnel en boma sous les étoiles africaines",
        "Marches exclusives dans la brousse à la découverte de la flore et des traces"
      ],
      "accommodations": "Chaque détail est conçu pour une intégration harmonieuse au cœur des écosystèmes, alliant luxe absolu, piscines rafraîchissantes et terrasses panoramiques.",
      "inclusions": [
        "Tous les safaris en 4x4 ouvert avec ranger et pisteur professionnels",
        "Hébergement en lodge de luxe avec repas gastronomiques et boissons premium",
        "Droits d'entrée et taxes de conservation du parc national Kruger",
        "Transferts panoramiques entre l'aéroport et le lodge"
      ],
      "requiredPreparations": [
        "Veuillez noter que le parc national Kruger est situé dans une zone impaludée. Des précautions / une prophylaxie contre le paludisme et une consultation médicale sont recommandées avant le départ."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Arrivée et Coucher de Soleil dans la Savane",
          "description": "Arrivée sur la piste privée et accueil chaleureux par l'équipe du lodge. Installation dans votre suite de luxe avant votre premier safari de fin d'après-midi.",
          "activities": [
            "Réception de Bienvenue",
            "Safari au Coucher du Soleil",
            "Dîner Boma au Coin du Feu"
          ]
        },
        {
          "day": 2,
          "title": "Pistage à l'Aube et Grands Prédateurs",
          "description": "Réveil avant l'aube autour d'un café africain. Départ à la recherche des lions et léopards actifs au lever du jour.",
          "activities": [
            "Safari au Lever du Jour",
            "Pause Café en Savane",
            "Observation des Félins l'Après-Midi"
          ]
        },
        {
          "day": 3,
          "title": "Exploration de la Brousse Profonde et Points d'Eau",
          "description": "Traversez des paysages variés, des forêts de mopanes aux grandes plaines. Observez les troupeaux d'éléphants se rafraîchir aux points d'eau.",
          "activities": [
            "Safaris 4x4 Quotidiens",
            "Pause Photo aux Points d'Eau",
            "Cocktail au Coucher du Soleil"
          ]
        },
        {
          "day": 4,
          "title": "Marche Guidée en Brousse et Dîner sous les Étoiles",
          "description": "Partez à pied avec un ranger armé pour décrypter les secrets de la nature : empreintes, plantes médicinales et cris des oiseaux.",
          "activities": [
            "Marche Guidée en Brousse",
            "Détente à la Piscine Privée",
            "Dîner Gastronomique Boma"
          ]
        },
        {
          "day": 5,
          "title": "Dernier Safari Matinal et Départ",
          "description": "Profitez d'un dernier safari au lever du soleil suivi d'un petit-déjeuner copieux avant votre transfert retour.",
          "activities": [
            "Safari au Lever du Soleil",
            "Brunch d'Adieu au Lodge",
            "Transfert Aéroport"
          ]
        }
      ]
    },
    "de": {
      "title": "Kruger Erlebnis – Big 5 Safari",
      "subtitle": "Ikonische Big 5 Pirschfahrten im weltberühmten Wildreservat Südafrikas",
      "location": "Krüger-Nationalpark",
      "country": "Südafrika",
      "durationText": "Empfohlen: 5 – 10 Tage",
      "difficulty": "Relaxed",
      "description": "Erleben Sie den legendären Krüger-Nationalpark in Südafrika. Begegnen Sie den berühmten Big 5 (Löwe, Leopard, Nashorn, Elefant und Büffel) in ihrem natürlichen Lebensraum auf spektakulären Pirschfahrten bei Sonnenaufgang und Dämmerung.",
      "highlights": [
        "Pirschfahrten im offenen 4x4-Geländewagen bei Sonnenaufgang und Dämmerung",
        "Zertifizierte Ranger und erfahrene Fährtenleser",
        "Traditionelles Boma-Abendessen unter dem afrikanischen Sternenhimmel",
        "Exklusive Buschwanderungen zur Erkundung der einheimischen Flora"
      ],
      "accommodations": "Jedes Detail wird für eine perfekte Integration in die umliegenden Ökosysteme ausgewählt – höchster Luxus, Erfrischungspools und erstklassige Speiseterrassen.",
      "inclusions": [
        "Alle Pirschfahrten im offenen Allradfahrzeug mit professionellem Ranger- & Tracker-Team",
        "Luxus-Lodge-Unterkunft mit Gourmet-Verpflegung und Premium-Getränken",
        "Naturschutzabgaben und Eintrittsgebühren für den Krüger-Nationalpark",
        "Panorama-Transfers zwischen Flughafen und Luxus-Lodge"
      ],
      "requiredPreparations": [
        "Bitte beachten Sie, dass der Krüger-Nationalpark in einem Malariagebiet liegt. Malaria-Vorsichtsmaßnahmen / Prophylaxe und eine ärztliche Beratung werden vor der Abreise empfohlen."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Ankunft & Sonnenuntergangs-Begrüßung im Busch",
          "description": "Ankunft auf der privaten Landebahn und Empfang durch die Lodge. Beziehen Sie Ihre Luxussuite vor der ersten Nachmittags-Pirschfahrt.",
          "activities": [
            "Empfang in der Konzession",
            "Dämmerungs-Safari",
            "Boma-Willkommensabendessen"
          ]
        },
        {
          "day": 2,
          "title": "Morgen-Spurensuche & Raubtier-Begegnungen",
          "description": "Erwachen Sie vor Sonnenaufgang mit frischem afrikanischem Kaffee. Spüren Sie Löwen und Leoparden auf, solange die Raubtiere aktiv sind.",
          "activities": [
            "Sonnenaufgangs-Pirschfahrt",
            "Kaffeepause in der Savanne",
            "Nachmittags-Wildbeobachtung"
          ]
        },
        {
          "day": 3,
          "title": "Tiefes Wildnis-Erlebnis & Wasserlöcher",
          "description": "Durchqueren Sie abwechslungsreiche Lebensräume von Mopane-Wäldern bis zu offenen Graslandschaften und beobachten Sie Elefanten an den Wasserlöchern.",
          "activities": [
            "Tägliche 4x4 Pirschfahrten",
            "Fotostopp am Wasserloch",
            "Sonnenuntergangs-Cocktail"
          ]
        },
        {
          "day": 4,
          "title": "Geführte Buschwanderung & Boma-Festmahl",
          "description": "Erkunden Sie die Natur zu Fuß mit einem bewaffneten Ranger und lernen Sie Tierspuren und Heilpflanzen kennen.",
          "activities": [
            "Geführte Buschwanderung",
            "Entspannung am privaten Pool",
            "Gourmet-Boma-Abendessen"
          ]
        },
        {
          "day": 5,
          "title": "Letzte Morgen-Pirschfahrt & Abschied",
          "description": "Genießen Sie eine letzte Frühmorgen-Safari vor einem herzhaften Frühstück und dem Transfer zu Ihrer Weiterreise.",
          "activities": [
            "Sonnenaufgangs-Safari",
            "Abschieds-Brunch",
            "Flughafentransfer"
          ]
        }
      ]
    }
  },
  "beach-to-big-5-safari": {
    "it": {
      "title": "Dalla Spiaggia ai Big 5 – Safari",
      "subtitle": "Le coste incontaminate dell'Oceano Indiano incontrano le riserve private Big 5",
      "location": "KwaZulu-Natal",
      "country": "Sudafrica",
      "durationText": "Consigliata: Minimo 7 Giorni",
      "difficulty": "Relaxed",
      "description": "Vivi il perfetto connubio nel KwaZulu-Natal: dalle calde spiagge dorate della costa e dune costiere alle riserve private Big 5 con fauna marina e predatori della savana.",
      "highlights": [
        "Unisci spiagge riservate dell'Oceano Indiano a riserve private Big 5",
        "Snorkeling e avvistamento delfini lungo barriere coralline subtropicali",
        "Safari giornalieri al mattino e al tramonto in concessioni private",
        "Aperitivi al tramonto con vista sugli orizzonti costieri e della savana"
      ],
      "accommodations": "Ogni dettaglio è scelto per la massima integrazione negli ecosistemi circostanti, preservando un lusso di altissimo livello. Piscine private, terrazze panoramiche e assistenza 24 ore su 24.",
      "inclusions": [
        "Tutti i soggiorni in lodge di lusso sulla costa e nella riserva",
        "Escursioni marine guidate e safari giornalieri in 4x4 aperti",
        "Cucina gourmet a base di pesce fresco e cene tradizionali nel bush",
        "Tutte le tasse di conservazione regionali e permessi di ingresso"
      ],
      "requiredPreparations": [
        "Essenziali per Safari e Spiaggia: Porta abbigliamento comodo da esterno, costumi da bagno e un'ottima protezione solare sia per il safari che per le attività costiere."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Arrivo sulla Costa Subtropicale dei Delfini",
          "description": "Arrivo sulla costa del KwaZulu-Natal e trasferimento al lodge sulla spiaggia riservata.",
          "activities": [
            "Check-in tra le Dune",
            "Passeggiata sulla Spiaggia",
            "Cena di Pesce Sotto le Stelle"
          ]
        },
        {
          "day": 2,
          "title": "Safari Marino ed Esplorazione delle Dune",
          "description": "Imbarco per un safari oceanico alla ricerca di delfini e snorkeling sulle barriere coralline.",
          "activities": [
            "Snorkeling Guidato",
            "Avvistamento Delfini",
            "Aperitivo in Spiaggia"
          ]
        },
        {
          "day": 3,
          "title": "Trasferimento verso la Riserva Privata Big 5",
          "description": "Viaggio panoramico verso l'interno attraverso le colline dello Zululand fino alla riserva naturale Big 5.",
          "activities": [
            "Trasferimento Panoramico",
            "Accoglienza al Lodge",
            "Safari Pomeridiano"
          ]
        },
        {
          "day": 4,
          "title": "Tracciamento Predatori e Safari nella Savana",
          "description": "Giornata dedicata al tracciamento di leoni, ghepardi e leopardi con il team di ranger e tracker.",
          "activities": [
            "Tracciamento all'Alba",
            "Picnic nel Bush",
            "Safari al Crepuscolo"
          ]
        },
        {
          "day": 5,
          "title": "Conservazione dei Rinoceronti e Specie Protette",
          "description": "Scopri i progetti di tutela e partecipa al tracciamento dei rinoceronti bianchi e neri.",
          "activities": [
            "Esperienza Tracciamento Rinoceronti",
            "Approfondimento Conservazione",
            "Serata al Fuoco"
          ]
        },
        {
          "day": 6,
          "title": "Passeggiata nel Bush e Banchetto in Boma",
          "description": "Emozione di un safari a piedi seguito da relax in piscina e grande cena in boma.",
          "activities": [
            "Safari a Piedi",
            "Relax in Terrazza",
            "Cena Tradizionale Braai"
          ]
        },
        {
          "day": 7,
          "title": "Safari all'Alba e Partenza",
          "description": "Ultimo safari al sorgere del sole prima della colazione e del trasferimento in aeroporto.",
          "activities": [
            "Ultimo Safari",
            "Ricca Colazione",
            "Trasferimento Aeroporto"
          ]
        }
      ]
    },
    "fr": {
      "title": "De la Plage aux Big 5 – Safari",
      "subtitle": "Les rivages sauvages de l'Océan Indien rencontrent les prestigieuses réserves Big 5",
      "location": "KwaZulu-Natal",
      "country": "Afrique du Sud",
      "durationText": "Recommandée : 7 Jours Minimum",
      "difficulty": "Relaxed",
      "description": "Vivez l'harmonie parfaite au KwaZulu-Natal : passez des plages préservées de la côte aux réserves privées Big 5 avec observation marine et grands félins.",
      "highlights": [
        "Combinez plages isolées de l'Océan Indien et réserves privées Big 5",
        "Snorkeling et observation des dauphins sur les récifs coralliens",
        "Safaris quotidiens matin et soir en concessions privées",
        "Cocktails au coucher du soleil sur les horizons côtiers et sauvages"
      ],
      "accommodations": "Chaque détail est choisi pour une intégration harmonieuse au cœur des écosystèmes, alliant luxe absolu, piscines rafraîchissantes et service 24h/24.",
      "inclusions": [
        "Hébergements en lodges de luxe sur la côte et en réserve",
        "Excursions marines guidées et safaris quotidiens en 4x4 ouvert",
        "Repas gastronomiques de fruits de mer et dîners traditionnels en brousse",
        "Toutes les taxes de conservation régionales et permis d'entrée"
      ],
      "requiredPreparations": [
        "Essentiels Safari et Plage : Emportez des vêtements d'extérieur confortables, des maillots de bain et une bonne protection solaire pour le safari et les activités côtières."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Arrivée sur la Côte Subtropicale",
          "description": "Arrivée sur la côte du KwaZulu-Natal et transfert vers votre lodge de plage privé.",
          "activities": [
            "Check-in aux Dunes",
            "Balade sur la Plage",
            "Dîner Fruits de Mer"
          ]
        },
        {
          "day": 2,
          "title": "Safari Marin et Récifs Coralliens",
          "description": "Embarquez pour un safari en mer à la rencontre des dauphins et faites du snorkeling.",
          "activities": [
            "Snorkeling Guidé",
            "Observation des Dauphins",
            "Cocktail en Bord de Mer"
          ]
        },
        {
          "day": 3,
          "title": "Traversée vers la Réserve Privée Big 5",
          "description": "Trajet panoramique à travers les collines du Zululand jusqu'à votre réserve de safari.",
          "activities": [
            "Trajet Panoramique",
            "Accueil au Lodge",
            "Safari de Fin d'Après-Midi"
          ]
        },
        {
          "day": 4,
          "title": "Pistage des Félins et Safaris Savane",
          "description": "Journée consacrée au pistage des lions, guépards et léopards avec votre équipe de guides.",
          "activities": [
            "Pistage Matinal",
            "Pique-Nique en Brousse",
            "Safari au Crépuscule"
          ]
        },
        {
          "day": 5,
          "title": "Protection des Rhinocéros et Espèces Menacées",
          "description": "Découvrez les projets de lutte anti-braconnage et suivez les rhinocéros sur le terrain.",
          "activities": [
            "Pistage des Rhinocéros",
            "Échanges Conservation",
            "Soirée au Coin du Feu"
          ]
        },
        {
          "day": 6,
          "title": "Marche en Brousse et Dîner Boma",
          "description": "Marche guidée à pied dans la brousse suivie d'un après-midi détente et d'un banquet boma.",
          "activities": [
            "Safari à Pied",
            "Détente au Lodge",
            "Barbecue Braai Traditionnel"
          ]
        },
        {
          "day": 7,
          "title": "Dernier Safari et Départ",
          "description": "Dernier safari au lever du soleil avant le petit-déjeuner et votre transfert retour.",
          "activities": [
            "Dernier Safari Matinal",
            "Petit-Déjeuner Copieux",
            "Transfert Aéroport"
          ]
        }
      ]
    },
    "de": {
      "title": "Vom Strand zu den Big 5 – Safari",
      "subtitle": "Unberührte Strände am Indischen Ozean treffen auf erstklassige Big 5 Reservate",
      "location": "KwaZulu-Natal",
      "country": "Südafrika",
      "durationText": "Empfohlen: Mindestens 7 Tage",
      "difficulty": "Relaxed",
      "description": "Erleben Sie die ideale Kombination aus den subtropischen Küsten von KwaZulu-Natal und exklusiven privaten Big 5 Reservaten mit maritimem Abenteuer und afrikanischer Wildnis.",
      "highlights": [
        "Einsame Strände am Indischen Ozean kombiniert mit exklusiven Big 5 Reservaten",
        "Schnorcheln und Delfinbeobachtungen an subtropischen Korallenriffen",
        "Tägliche Frühmorgens- und Abend-Pirschfahrten in privaten Schutzgebieten",
        "Sonnenuntergangs-Drinks mit Blick auf Küsten- und Savannen-Horizonte"
      ],
      "accommodations": "Jedes Detail wird für eine perfekte Integration in die umliegenden Ökosysteme ausgewählt – Höchster Luxus, Erfrischungspools und 24-Stunden-Service.",
      "inclusions": [
        "Unterkünfte in Luxus-Lodges an der Küste und im Wildreservat",
        "Geführte Meeresausflüge und tägliche Pirschfahrten im offenen Allradfahrzeug",
        "Meeresfrüchte-Gourmetküche und traditionelle Busch-Abendessen",
        "Alle regionalen Naturschutzgebühren und Eintrittsgenehmigungen"
      ],
      "requiredPreparations": [
        "Safari- & Strand-Essentials: Packen Sie bequeme Outdoor-Kleidung, Bademode und guten Sonnenschutz sowohl für Safari- als auch für Küstenaktivitäten ein."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Ankunft an der Delphinküste",
          "description": "Ankunft an der Küste von KwaZulu-Natal und Transfer zu Ihrer exklusiven Strandlodge.",
          "activities": [
            "Check-in am Strand",
            "Strandspaziergang",
            "Meeresfrüchte-Abendessen"
          ]
        },
        {
          "day": 2,
          "title": "Meeres-Safari & Korallenriff-Erkundung",
          "description": "Bootsausflug zur Delfinbeobachtung und Schnorcheln an farbenprächtigen Riffen.",
          "activities": [
            "Geführtes Schnorcheln",
            "Delfinbeobachtung",
            "Sonnenuntergang am Strand"
          ]
        },
        {
          "day": 3,
          "title": "Fahrt ins Big 5 Wildreservat",
          "description": "Panoramafahrt durch die Hügel des Zululands direkt in Ihr privates Big 5 Reservat.",
          "activities": [
            "Panorama-Transfer",
            "Empfang in der Lodge",
            "Nachmittags-Pirschfahrt"
          ]
        },
        {
          "day": 4,
          "title": "Raubtier-Spurensuche & Savannen-Safaris",
          "description": "Verbringen Sie den Tag mit der Suche nach Löwen, Geparden und Leoparden.",
          "activities": [
            "Frühmorgens-Spurensuche",
            "Busch-Picknick",
            "Dämmerungs-Pirschfahrt"
          ]
        },
        {
          "day": 5,
          "title": "Nashorn-Schutz & Artenschutz-Einblicke",
          "description": "Lernen Sie Anti-Wilderer-Initiativen kennen und spüren Sie Breit- und Spitzmaulnashörner auf.",
          "activities": [
            "Nashorn-Tracking",
            "Artenschutz-Einblicke",
            "Lagerfeuer-Gespräche"
          ]
        },
        {
          "day": 6,
          "title": "Buschwanderung & Boma-Festmahl",
          "description": "Buschwanderung zu Fuß mit einem Ranger, gefolgt von Entspannung und einem Braai-Festmahl.",
          "activities": [
            "Fuß-Safari",
            "Entspannung auf der Terrasse",
            "Traditionelles Braai-Abendessen"
          ]
        },
        {
          "day": 7,
          "title": "Sonnenaufgangs-Safari & Abreise",
          "description": "Letzte Sonnenaufgangs-Pirschfahrt vor dem Frühstück und Transfer zum Flughafen.",
          "activities": [
            "Letzte Pirschfahrt",
            "Herzhaftes Frühstück",
            "Flughafentransfer"
          ]
        }
      ]
    }
  },
  "big-5-mountain-drakensberg": {
    "it": {
      "title": "Safari Big 5 e Fuga sui Monti Drakensberg",
      "subtitle": "I maestosi anfiteatri montani dei Drakensberg e riserve private Big 5",
      "location": "KwaZulu-Natal",
      "country": "Sudafrica",
      "durationText": "Consigliata: Minimo 7 Giorni",
      "difficulty": "Moderate",
      "description": "Unisci le vette del patrimonio mondiale dell'UNESCO del Drakensberg all'emozione di un safari privato Big 5. Fai trekking tra pareti di basalto e cascate, scopri l'arte rupestre San e rilassati in boutique lodge montani prima del safari.",
      "highlights": [
        "Esplora l'iconico Anfiteatro dei Drakensberg e le gole del fiume Tugela",
        "Passeggiate culturali guidate alla scoperta dell'arte rupestre degli antichi San",
        "Tracciamento dei Big 5 nelle migliori riserve private del KwaZulu-Natal",
        "Soggiorni panoramici in montagna abbinati a cene gourmet al camino"
      ],
      "accommodations": "Ogni dettaglio è scelto per la massima integrazione negli ecosistemi circostanti. Piscine panoramiche, terrazze e cucina d'eccellenza.",
      "inclusions": [
        "Soggiorni in boutique lodge di montagna e safari lodge di lusso",
        "Trekking guidati in montagna e safari giornalieri in 4x4 aperti",
        "Tutti i pasti, vini selezionati e menu degustazione regionali",
        "Permessi per il parco del Drakensberg e tasse di conservazione della riserva"
      ],
      "requiredPreparations": [
        "Essenziali per Montagna e Safari: Porta scarpe comode da camminata e abbigliamento a strati, poiché le temperature in montagna possono cambiare rapidamente."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Benvenuti tra le Vette del Drakensberg",
          "description": "Arrivo ai piedi del Drakensberg settentrionale e check-in nel lodge con vista sulle pareti di basalto.",
          "activities": [
            "Check-in al Lodge di Montagna",
            "Passeggiata al Tramonto",
            "Cena al Camino"
          ]
        },
        {
          "day": 2,
          "title": "Cascate, Vette e Arte Rupestre San",
          "description": "Trekking guidato verso cascate di montagna e rifugi con antiche pitture rupestri del popolo San.",
          "activities": [
            "Trekking di Montagna",
            "Arte Rupestre San",
            "Tè Pomeridiano Panoramico"
          ]
        },
        {
          "day": 3,
          "title": "Trasferimento Panoramico verso la Riserva Big 5",
          "description": "Discesa dalle montagne attraverso le colline verso la riserva privata Big 5 nel KwaZulu-Natal.",
          "activities": [
            "Traversata delle Midlands",
            "Accoglienza al Safari Lodge",
            "Primo Safari al Tramonto"
          ]
        },
        {
          "day": 4,
          "title": "Tracciamento Grandi Feline ed Elefanti",
          "description": "Safari nella savana alla ricerca di leoni, leopardi, rinoceronti ed elefanti.",
          "activities": [
            "Safari all'Alba",
            "Brunch nella Savana",
            "Tracciamento Notturno"
          ]
        },
        {
          "day": 5,
          "title": "Esplorazione della Riserva ed Aperitivi al Tramonto",
          "description": "Esplora angoli remoti della riserva dove i ghepardi cacciano nelle pianure aperte.",
          "activities": [
            "Spedizione Big 5",
            "Birdwatching alle Pozze",
            "Aperitivo in Collina"
          ]
        },
        {
          "day": 6,
          "title": "Passeggiata nel Bushveld e Stelle in Boma",
          "description": "Impara l'arte del tracciamento a piedi con ranger esperti, seguito da cena sotto le stelle.",
          "activities": [
            "Safari a Piedi",
            "Relax in Piscina",
            "Banchetto Sotto le Stelle"
          ]
        },
        {
          "day": 7,
          "title": "Ultimo Safari all'Alba e Partenza",
          "description": "Ultimo emozionante safari mattutino prima della colazione e del trasferimento in aeroporto.",
          "activities": [
            "Safari all'Alba",
            "Colazione di Saluto",
            "Trasferimento Aeroporto"
          ]
        }
      ]
    },
    "fr": {
      "title": "Safari Big 5 et Évasion dans les Montagnes du Drakensberg",
      "subtitle": "Les amphithéâtres majestueux du Drakensberg et les réserves privées Big 5",
      "location": "KwaZulu-Natal",
      "country": "Afrique du Sud",
      "durationText": "Recommandée : 7 Jours Minimum",
      "difficulty": "Moderate",
      "description": "Associez les sommets spectaculaires du Drakensberg (UNESCO) au frisson d'un safari privé Big 5. Randonnez au pied des falaises de basalte, découvrez l'art rupestre San et détendez-vous dans des lodges d'exception.",
      "highlights": [
        "Explorez l'amphithéâtre du Drakensberg et les gorges de la rivière Tugela",
        "Randonnées culturelles avec guides spécialisés dans l'art rupestre San",
        "Pistage des Big 5 dans les meilleures réserves privées du KwaZulu-Natal",
        "Lodges de montagne panoramiques et dîners gastronomiques au coin du feu"
      ],
      "accommodations": "Chaque détail est choisi pour une intégration harmonieuse au cœur de la nature. Hébergements d'exception avec piscines et terrasses vues montagnes.",
      "inclusions": [
        "Séjours en lodges de montagne et lodges de safari de luxe",
        "Randonnées guidées en montagne et safaris quotidiens en 4x4 ouvert",
        "Tous les repas, sélections de vins et menus régionaux",
        "Permis du parc du Drakensberg et taxes de conservation"
      ],
      "requiredPreparations": [
        "Essentiels Montagne et Safari : Apportez des chaussures de marche confortables et des vêtements superposables, car les températures en montagne peuvent changer rapidement."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Bienvenue dans les Montagnes du Drakensberg",
          "description": "Arrivée dans les contreforts du Drakensberg et installation dans votre lodge avec vue sur les falaises.",
          "activities": [
            "Check-in au Lodge de Montagne",
            "Marche au Coucher du Soleil",
            "Dîner au Coin du Feu"
          ]
        },
        {
          "day": 2,
          "title": "Cascades, Sommets et Art Rupestre San",
          "description": "Randonnée guidée vers les cascades et les grottes abritant des peintures rupestres San ancestrales.",
          "activities": [
            "Randonnée Guidée",
            "Art Rupestre San",
            "Thé Panoramique"
          ]
        },
        {
          "day": 3,
          "title": "Trajet Panoramique vers la Réserve Big 5",
          "description": "Descente des sommets à travers les collines vers votre réserve privée de safari.",
          "activities": [
            "Trajet Panoramique",
            "Accueil au Safari Lodge",
            "Premier Safari au Crépuscule"
          ]
        },
        {
          "day": 4,
          "title": "Pistage des Grands Félins et Éléphants",
          "description": "Safaris dans la savane à la recherche des lions, léopards, rhinocéros et éléphants.",
          "activities": [
            "Safari au Lever du Jour",
            "Brunch Savane",
            "Pistage Nocturne"
          ]
        },
        {
          "day": 5,
          "title": "Grand Safari et Cocktails au Coucher du Soleil",
          "description": "Parcourez les zones reculées de la réserve où évoluent les guépards et les girafes.",
          "activities": [
            "Expédition Big 5",
            "Observation aux Points d'Eau",
            "Cocktail en Hauteur"
          ]
        },
        {
          "day": 6,
          "title": "Marche en Brousse et Dîner Boma",
          "description": "Apprenez le pistage à pied avec des rangers professionnels, suivi d'un dîner sous les étoiles.",
          "activities": [
            "Safari à Pied",
            "Détente au Lodge",
            "Dîner sous les Étoiles"
          ]
        },
        {
          "day": 7,
          "title": "Dernier Safari et Départ",
          "description": "Dernier safari matinal avant le petit-déjeuner et le transfert retour vers l'aéroport.",
          "activities": [
            "Safari Matinal",
            "Petit-Déjeuner d'Adieu",
            "Transfert Aéroport"
          ]
        }
      ]
    },
    "de": {
      "title": "Big 5 Safari & Bergwelten in den Drakensbergen",
      "subtitle": "Majestätische Drakensberg-Gipfel und exklusive private Big 5 Reservate",
      "location": "KwaZulu-Natal",
      "country": "Südafrika",
      "durationText": "Empfohlen: Mindestens 7 Tage",
      "difficulty": "Moderate",
      "description": "Verbinden Sie die gewaltigen Berge des Drakensberg-Weltkulturerbes mit dem Nervenkitzel einer privaten Big 5 Safari. Wandern Sie vorbei an Wasserfällen und uralter Felskunst der San.",
      "highlights": [
        "Erkunden Sie das Drakensberg-Amphitheater und die spektakuläre Tugela-Schlucht",
        "Kulturelle Felskunst-Wanderungen mit spezialisierten Guides",
        "Big 5 Spurensuche in den führenden privaten Wildreservaten von KwaZulu-Natal",
        "Atemberaubende Panorama-Berglodges kombiniert mit feiner Gourmetküche"
      ],
      "accommodations": "Jedes Detail wird für eine perfekte Integration in die umliegenden Ökosysteme ausgewählt – Höchster Luxus, Bergpools und Speiseterrassen.",
      "inclusions": [
        "Aufenthalte in feinen Berglodges und luxuriösen Safari-Lodges",
        "Geführte Bergwanderungen und tägliche Pirschfahrten im offenen Allradfahrzeug",
        "Alle Mahlzeiten, ausgewählte Weine und regionale Degustationsmenüs",
        "Drakensberg-Parkgenehmigungen und Naturschutzabgaben"
      ],
      "requiredPreparations": [
        "Berg- & Safari-Essentials: Bringen Sie bequeme Wanderschuhe und Kleidung im Zwiebellook mit, da sich die Temperaturen in den Bergen schnell ändern können."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Willkommen in den Drakensbergen",
          "description": "Ankunft am Fuße der nördlichen Drakensberge und Check-in in Ihrer Lodge mit Blick auf die Basaltfelsen.",
          "activities": [
            "Check-in in der Berglodge",
            "Abendspaziergang im Tal",
            "Gemütliches Abendessen am Kamin"
          ]
        },
        {
          "day": 2,
          "title": "Wasserfälle & Felskunst der San",
          "description": "Geführte Wanderung zu Bergwasserfällen und Höhlen mit uralten Malereien des San-Volkes.",
          "activities": [
            "Geführte Bergwanderung",
            "San-Felskunst-Führung",
            "Nachmittagstee mit Panoramablick"
          ]
        },
        {
          "day": 3,
          "title": "Fahrt ins Big 5 Wildreservat",
          "description": "Fahrt von den Bergspitzen durch die sanften Hügel direkt in Ihr privates Big 5 Wildschutzgebiet.",
          "activities": [
            "Panoramafahrt",
            "Empfang in der Safari-Lodge",
            "Erste Dämmerungs-Pirschfahrt"
          ]
        },
        {
          "day": 4,
          "title": "Großkatzen & Elefanten-Spurensuche",
          "description": "Pirschfahrten durch die Savanne auf der Suche nach Löwen, Leoparden, Nashörnern und Elefanten.",
          "activities": [
            "Frühmorgens-Pirschfahrt",
            "Savannen-Brunch",
            "Nacht-Safari mit Scheinwerfer"
          ]
        },
        {
          "day": 5,
          "title": "Große Reservat-Erkundung & Sonnenuntergang",
          "description": "Erkunden Sie entlegene Winkel des Reservats, wo Geparde auf den offenen Ebenen jagen.",
          "activities": [
            "Big 5 Expedition",
            "Vogelbeobachtung",
            "Sonnenuntergangs-Drinks"
          ]
        },
        {
          "day": 6,
          "title": "Buschwanderung & Sternenhimmel-Festmahl",
          "description": "Erlernen Sie das Fährtenlesen zu Fuß mit erfahrenen Rangern, gefolgt von einem Gourmet-Festmahl.",
          "activities": [
            "Fuß-Safari",
            "Entspannung am Pool",
            "Sternenhimmel-Banquet"
          ]
        },
        {
          "day": 7,
          "title": "Morgen-Pirschfahrt & Abreise",
          "description": "Eine letzte Frühmorgens-Pirschfahrt vor dem Frühstück und Transfer zum Flughafen.",
          "activities": [
            "Morgen-Pirschfahrt",
            "Abschiedsfrühstück",
            "Flughafentransfer"
          ]
        }
      ]
    }
  },
  "cape-town-big-5-safari": {
    "it": {
      "title": "Città del Capo e Safari Big 5",
      "subtitle": "Panorami atlantici cosmopoliti, Table Mountain e riserve faunistiche Big 5",
      "location": "Western Cape, Parco Nazionale Kruger, KwaZulu-Natal",
      "country": "Sudafrica",
      "durationText": "Consigliata: Minimo 9 Giorni",
      "difficulty": "Relaxed",
      "description": "La fusione perfetta tra eleganza urbana e natura selvaggia africana. Inizia a Città del Capo circondato dalla Table Mountain e dalla costa atlantica, esplora i vigneti storici e le colonie di pinguini a Cape Point, poi vola nelle riserve Big 5.",
      "highlights": [
        "Salita in funivia privata sulla Table Mountain e tour della penisola di Cape Point",
        "Degustazione esclusiva di vini nelle cantine storiche di Franschhoek e Stellenbosch",
        "Safari giornalieri nei migliori lodge di riserve private Big 5",
        "Incontro con la colonia di pinguini africani a Boulders Beach"
      ],
      "accommodations": "Ogni dettaglio è scelto per la massima integrazione negli ecosistemi circostanti. Hotel boutique 5 stelle a Città del Capo e safari lodge di ultra-lusso.",
      "inclusions": [
        "Soggiorno in boutique hotel 5 stelle a Città del Capo e lodge di lusso nella riserva",
        "Escursioni private guidate nella Penisola del Capo e nelle Terre del Vino",
        "Tutti i safari in veicoli 4x4 aperti con ranger e tracker esperti",
        "Tutte le tasse dei parchi nazionali, conservazione e cene gourmet"
      ],
      "requiredPreparations": [
        "Meteo Variabile: Porta abiti a strati, calzature comode e protezione solare per il clima variabile di Città del Capo e per il safari."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Arrivo a Città del Capo e Benvenuto al V&A Waterfront",
          "description": "Arrivo nella Mother City e trasferimento nel boutique hotel sul Waterfront con vista sulla Table Mountain.",
          "activities": [
            "Accoglienza in Aeroporto",
            "Cocktail sul Waterfront",
            "Cena al Porto"
          ]
        },
        {
          "day": 2,
          "title": "Salita sulla Table Mountain e Tour del Centro Storico",
          "description": "Salita in funivia sulla Table Mountain per una vista a 360 gradi sull'Atlantico, seguita dal tour di Bo-Kaap.",
          "activities": [
            "Funivia Table Mountain",
            "Tour Culturale Bo-Kaap",
            "Aperitivo a Camps Bay"
          ]
        },
        {
          "day": 3,
          "title": "Penisola del Capo, Pinguini a Boulders Beach e Cape Point",
          "description": "Percorri la panoramica strada di Chapman’s Peak, visita i pinguini a Boulders Beach e scopri Cape Point.",
          "activities": [
            "Strada Panoramica Chapman’s Peak",
            "Colonia Pinguini Boulders Beach",
            "Capo di Buona Speranza"
          ]
        },
        {
          "day": 4,
          "title": "Degustazione Privata nei Vigneti di Franschhoek e Stellenbosch",
          "description": "Giornata tra le tenute vinicole storiche con degustazione di vini premiati e pranzo nei vigneti.",
          "activities": [
            "Visita Cantine Storiche",
            "Degustazione con Sommelier",
            "Tour dei Vigneti"
          ]
        },
        {
          "day": 5,
          "title": "Volo verso la Riserva Privata Big 5",
          "description": "Volo verso le migliori riserve private sudafricane e arrivo al safari lodge in tempo per il safari pomeridiano.",
          "activities": [
            "Volo Interno",
            "Arrivo nella Riserva",
            "Safari Big 5 al Tramonto"
          ]
        },
        {
          "day": 6,
          "title": "Tracciamento Grandi Predatori e Safari Notturno",
          "description": "Safari all'alba per seguire i branchi di leoni e i leopardi, seguito da brunch nel bush e relax.",
          "activities": [
            "Safari Predatori all'Alba",
            "Brunch sotto le Acacie",
            "Safari Notturno con Faro"
          ]
        },
        {
          "day": 7,
          "title": "Safari nella Savana Profonda e Pranzo con Champagne",
          "description": "Incontra grandi branchi di elefanti, rinoceronti neri e varie specie di volatili.",
          "activities": [
            "Safari del Mattino",
            "Incontro con gli Elefanti",
            "Aperitivo sul Fiume"
          ]
        },
        {
          "day": 8,
          "title": "Safari Notturno e Cena di Gala al Fuoco in Boma",
          "description": "Combinazione di safari a piedi e in veicolo 4x4, culminante nella cena di gala in boma.",
          "activities": [
            "Passeggiata Guidata",
            "Safari Pomeridiano",
            "Gran Banchetto Boma"
          ]
        },
        {
          "day": 9,
          "title": "Ultimo Safari all'Alba e Partenza",
          "description": "Ultimo safari al sorgere del sole prima della colazione gourmet e del trasferimento in aeroporto.",
          "activities": [
            "Ultimo Safari all'Alba",
            "Colazione di Saluto",
            "Trasferimento Aeroporto"
          ]
        }
      ]
    },
    "fr": {
      "title": "Le Cap et Safari Big 5",
      "subtitle": "Vues atlantiques cosmopolites, Table Mountain et sanctuaires sauvages Big 5",
      "location": "Western Cape, Parc National Kruger, KwaZulu-Natal",
      "country": "Afrique du Sud",
      "durationText": "Recommandée : 9 Jours Minimum",
      "difficulty": "Relaxed",
      "description": "L'alliance parfaite entre élégance urbaine et brousse africaine préservée. Commencez au Cap au pied de Table Mountain, explorez la route des vins, observez les manchots à Cape Point puis envolez-vous pour un safari Big 5 d'exception.",
      "highlights": [
        "Ascension en téléphérique de Table Mountain et tour de la péninsule du Cap",
        "Dégustations exclusives de vins dans les domaines de Franschhoek & Stellenbosch",
        "Safaris quotidiens Big 5 dans des réserves privées prestigieuses",
        "Observation de la colonie de manchots africains à Boulders Beach"
      ],
      "accommodations": "Chaque détail est choisi pour une intégration harmonieuse au cœur des écosystèmes. Hôtel boutique 5 étoiles au Cap et lodge de safari ultra-luxe.",
      "inclusions": [
        "Hôtel boutique 5 étoiles au Cap et lodge de safari ultra-luxe",
        "Excursions privées guidées sur la péninsule du Cap et la route des vins",
        "Tous les safaris en 4x4 ouvert avec ranger et pisteur diplômés",
        "Tous les repas gastronomiques pendant le safari et droits d'entrée aux parcs"
      ],
      "requiredPreparations": [
        "Météo Variable : Prévoyez des vêtements superposables, des chaussures confortables et une protection solaire pour le temps variable du Cap et votre safari."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Arrivée au Cap et Accueil au V&A Waterfront",
          "description": "Arrivée dans la ville du Cap et transfert à votre hôtel de luxe sur le front de mer avec vue sur Table Mountain.",
          "activities": [
            "Accueil Personnalisé",
            "Cocktail de Bienvenue",
            "Dîner au Port"
          ]
        },
        {
          "day": 2,
          "title": "Ascension de Table Mountain et Quartier Bo-Kaap",
          "description": "Téléphérique rotatif jusqu'au sommet de Table Mountain puis visite guidée du quartier historique de Bo-Kaap.",
          "activities": [
            "Téléphérique Table Mountain",
            "Tour Culturel Bo-Kaap",
            "Cocktail au Coucher du Soleil"
          ]
        },
        {
          "day": 3,
          "title": "Péninsule du Cap, Manchots de Boulders Beach & Cape Point",
          "description": "Route panoramique de Chapman’s Peak, visite aux manchots de Boulders Beach et falaises du Cap de Bonne-Espérance.",
          "activities": [
            "Route Chapman’s Peak",
            "Sanctuaire des Manchots",
            "Cap de Bonne-Espérance"
          ]
        },
        {
          "day": 4,
          "title": "Dégustations Privées dans les Domaines Viticoles",
          "description": "Journée dans la région des vins de Franschhoek et Stellenbosch avec dégustations privées et déjeuner gastronomique.",
          "activities": [
            "Visites des Caves Privées",
            "Dégustation avec Sommelier",
            "Déjeuner dans les Vignes"
          ]
        },
        {
          "day": 5,
          "title": "Vol vers la Réserve Privée Big 5",
          "description": "Vol intérieur vers les réserves privées et arrivée au lodge de safari pour votre premier safari de fin d'après-midi.",
          "activities": [
            "Vol Intérieur",
            "Arrivée au Lodge",
            "Safari Big 5 du Soir"
          ]
        },
        {
          "day": 6,
          "title": "Pistage des Prédateurs et Safari Nocturne",
          "description": "Départ à l'aube sur les traces des lions et léopards suivi d'un brunch et d'un safari nocturne.",
          "activities": [
            "Pistage Matinal",
            "Brunch sous les Acacias",
            "Safari Nocturne au Projecteur"
          ]
        },
        {
          "day": 7,
          "title": "Grands Troupeaux d'Éléphants et Coucher du Soleil",
          "description": "Explorez la savane et rencontrez de grands troupeaux d'éléphants et rhinocéros noirs.",
          "activities": [
            "Safari Grand Format",
            "Rencontre Éléphants",
            "Cocktail au Bord de la Rivière"
          ]
        },
        {
          "day": 8,
          "title": "Marche en Brousse et Dîner de Gala Boma",
          "description": "Marche guidée à pied et safari au crépuscule suivis d'un grand banquet boma autour du feu.",
          "activities": [
            "Marche en Brousse",
            "Safari du Soir",
            "Grand Banquet Boma"
          ]
        },
        {
          "day": 9,
          "title": "Dernier Safari Matinal et Départ",
          "description": "Dernier safari au lever du soleil avant le petit-déjeuner et votre transfert pour le vol retour.",
          "activities": [
            "Dernier Safari Matinal",
            "Petit-Déjeuner d'Adieu",
            "Transfert Aéroport"
          ]
        }
      ]
    },
    "de": {
      "title": "Kapstadt & Big 5 Safari",
      "subtitle": "Atlantik-Panoramen, Tafelberg und erstklassige Big 5 Wildschutzgebiete",
      "location": "Western Cape, Krüger-Nationalpark, KwaZulu-Natal",
      "country": "Südafrika",
      "durationText": "Empfohlen: Mindestens 9 Tage",
      "difficulty": "Relaxed",
      "description": "Die perfekte Kombination aus urbanem Flair und afrikanischer Wildnis. Beginnen Sie in Kapstadt am Tafelberg, erkunden Sie die Winelands und Pinguin-Kolonien am Kap und fliegen Sie dann zur Big 5 Safari.",
      "highlights": [
        "Auffahrt auf den Tafelberg mit der Seilbahn & Kap-Halbinsel-Tour",
        "Exklusive Weinverkostungen in historischen Gütern in Franschhoek & Stellenbosch",
        "Tägliche Big 5 Pirschfahrten in erstklassigen privaten Wildreservaten",
        "Besuch der afrikanischen Pinguin-Kolonie am Boulders Beach"
      ],
      "accommodations": "Jedes Detail wird für eine perfekte Integration in die Ökosysteme ausgewählt. 5-Sterne Boutique-Hotel in Kapstadt und Ultra-Luxus Safari Lodge.",
      "inclusions": [
        "5-Sterne Boutique-Hotel in Kapstadt & Ultra-Luxus Safari-Lodge",
        "Private geführte Ausflüge zur Kap-Halbinsel und in die Winelands",
        "Alle Pirschfahrten im offenen Allradfahrzeug mit Ranger & Tracker",
        "Alle Nationalpark-Gebühren, Naturschutzabgaben und Gourmet-Verpflegung"
      ],
      "requiredPreparations": [
        "Wechselhaftes Wetter: Packen Sie Kleidung im Zwiebellook, bequemes Schuhwerk und Sonnenschutz für das wechselhafte Wetter in Kapstadt und Ihre Safari ein."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Ankunft in Kapstadt & V&A Waterfront",
          "description": "Ankunft in der Mutterstadt und Transfer zu Ihrem Luxushotel an der Waterfront mit Blick auf den Tafelberg.",
          "activities": [
            "Flughafen-Empfang",
            "Willkommens-Cocktail",
            "Gourmet-Abendessen am Hafen"
          ]
        },
        {
          "day": 2,
          "title": "Tafelberg & Historische Stadtführung",
          "description": "Seilbahnfahrt auf den Tafelberg für Panoramablicke über den Atlantik, gefolgt von einer Führung durch Bo-Kaap.",
          "activities": [
            "Tafelberg-Seilbahn",
            "Kulturführung Bo-Kaap",
            "Sonnenuntergangs-Drinks in Camps Bay"
          ]
        },
        {
          "day": 3,
          "title": "Kap-Halbinsel, Pinguine & Kap der Guten Hoffnung",
          "description": "Fahrt über die Panoramastraße Chapman’s Peak, Besuch der Pinguine am Boulders Beach und Felsen am Kap Point.",
          "activities": [
            "Chapman’s Peak Drive",
            "Pinguine am Boulders Beach",
            "Kap der Guten Hoffnung"
          ]
        },
        {
          "day": 4,
          "title": "Exklusive Weinprobe in Franschhoek & Stellenbosch",
          "description": "Ein unvergesslicher Tag in historischen Weingütern mit Verkostung prämierter Weine und Gourmet-Mittagessen.",
          "activities": [
            "Weinkeller-Führungen",
            "Sommelier-Weinprobe",
            "Mittagessen im Weinberg"
          ]
        },
        {
          "day": 5,
          "title": "Flug ins Big 5 Wildreservat",
          "description": "Flug in Südafrikas führende private Wildreservate und Ankunft in Ihrer Luxus-Safari-Lodge.",
          "activities": [
            "Inlandsflug-Transfer",
            "Empfang in der Lodge",
            "Erste Nachmittags-Pirschfahrt"
          ]
        },
        {
          "day": 6,
          "title": "Spurensuche nach Raubtieren & Nacht-Safari",
          "description": "Frühmorgens-Pirschfahrt zu den Löwenrudeln und Leoparden, gefolgt von Busch-Brunch und Nachtsafari.",
          "activities": [
            "Sonnenaufgangs-Pirschfahrt",
            "Busch-Brunch unter Akazien",
            "Nacht-Safari mit Scheinwerfer"
          ]
        },
        {
          "day": 7,
          "title": "Große Elefantenherden & Fluss-Romantik",
          "description": "Entdecken Sie die Artenvielfalt der Savanne mit großen Elefantenherden und Nashörnern.",
          "activities": [
            "Morgen-Safari",
            "Elefanten-Begegnung",
            "Sonnenuntergangs-Drinks am Fluss"
          ]
        },
        {
          "day": 8,
          "title": "Buschwanderung & Festliches Boma-Abendessen",
          "description": "Eine Kombination aus Buschwanderung zu Fuß und Abend-Pirschfahrt mit festlichem Boma-Banquet.",
          "activities": [
            "Geführte Fuß-Safari",
            "Nachmittags-Pirschfahrt",
            "Traditionelles Boma-Festmahl"
          ]
        },
        {
          "day": 9,
          "title": "Letzte Morgen-Pirschfahrt & Abreise",
          "description": "Genießen Sie eine letzte Safari bei Sonnenaufgang vor dem Frühstück und Transfer zum Flughafen.",
          "activities": [
            "Sonnenaufgangs-Safari",
            "Abschiedsfrühstück",
            "Flughafentransfer"
          ]
        }
      ]
    }
  },
  "full-experience-cape-village-beach-safari": {
    "it": {
      "title": "The Full Experience – Città del Capo, St Lucia, Spiaggia, Safari e Big 5",
      "subtitle": "Il grand tour del Sudafrica: meraviglie del Capo, St Lucia, spiagge e Big 5",
      "location": "Western Cape e KwaZulu-Natal",
      "country": "Sudafrica",
      "badge": "Bestseller",
      "durationText": "Consigliata: Minimo 9 Giorni",
      "difficulty": "Relaxed",
      "description": "Una grande spedizione completa per celebrare l'anima variegata del Sudafrica. Scopri Città del Capo, siti patrimonio dell'UNESCO, crociere fluviali con ippopotami, rilassati sulle spiagge dell'Oceano Indiano e vivi safari Big 5.",
      "highlights": [
        "Esplora Città del Capo, la Table Mountain e la Penisola del Capo",
        "Immersione culturale autentica in un villaggio tradizionale Zulu",
        "Relax sulle spiagge calde e incontaminate dell'Oceano Indiano nel KwaZulu-Natal",
        "Safari Big 5 senza eguali in riserve private esenti da malaria"
      ],
      "accommodations": "Ogni dettaglio è scelto per la massima integrazione negli ecosistemi circostanti. Hotel di lusso a Città del Capo, resort sulla spiaggia e safari lodge.",
      "inclusions": [
        "Soggiorni di lusso a Città del Capo, resort sulla costa e riserve safari",
        "Tour culturali privati guidati con anziani del villaggio Zulu",
        "Tutti i safari in veicoli 4x4 aperti con naturalisti e tracker",
        "Pensione completa gourmet in safari e tutti i permessi di conservazione"
      ],
      "requiredPreparations": [
        "Preparati alla Varietà: Porta abiti versatili, calzature comode, costumi da bagno e protezione solare per i diversi ambienti durante il tuo viaggio."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Benvenuti a Città del Capo",
          "description": "Arrivo a Città del Capo e trasferimento nel prestigioso hotel sul Waterfront.",
          "activities": [
            "Accoglienza VIP",
            "Check-in Suite Lusso",
            "Cena sul Fronte Mare"
          ]
        },
        {
          "day": 2,
          "title": "Table Mountain e la Costa Atlantica",
          "description": "Salita in funivia sulla Table Mountain e tour panoramico di Camps Bay e Llandudno.",
          "activities": [
            "Vetta Table Mountain",
            "Strada Atlantica",
            "Cena al Tramonto a Camps Bay"
          ]
        },
        {
          "day": 3,
          "title": "Penisola del Capo e Pinguini a Boulders Beach",
          "description": "Esplora la Penisola del Capo, incontra i pinguini a Boulders Beach e scopri Cape Point.",
          "activities": [
            "Chapman’s Peak Drive",
            "Santuario dei Pinguini",
            "Trekking a Capo di Buona Speranza"
          ]
        },
        {
          "day": 4,
          "title": "Volo verso il KwaZulu-Natal e Check-in sulla Costa",
          "description": "Volo verso la costa subtropicale e check-in nel resort di lusso vista oceano.",
          "activities": [
            "Volo Interno",
            "Arrivo Resort Oceanico",
            "Tè Pomeridiano in Terrazza"
          ]
        },
        {
          "day": 5,
          "title": "Cultura Tradizionale Zulu nel Villaggio",
          "description": "Giornata al villaggio Zulu alla scoperta di tradizioni secolari, artigianato, danze e musica.",
          "activities": [
            "Accoglienza Tradizionale",
            "Raccenti degli Anziani",
            "Dimostrazione di Musica e Artigianato"
          ]
        },
        {
          "day": 6,
          "title": "Relax Subtropicale e Crociera Fiumara Ippopotami",
          "description": "Giornata di relax sulla spiaggia e crociera al tramonto tra gli ippopotami a St Lucia.",
          "activities": [
            "Passeggiata sulla Costa",
            "Crociera Ippopotami",
            "Escursione a Cape Vidal"
          ]
        },
        {
          "day": 7,
          "title": "Trasferimento alla Riserva Privata Big 5",
          "description": "Viaggio nell'interno verso l'esclusivo safari lodge per il primo safari al tramonto.",
          "activities": [
            "Trasferimento Panoramico",
            "Accoglienza al Lodge",
            "Safari Big 5 al Tramonto"
          ]
        },
        {
          "day": 8,
          "title": "Tracciamento dei Big 5: Leoni, Leopardi, Elefanti e Rinoceronti",
          "description": "Unisciti ai tracker professionisti alla ricerca dei grandi mammiferi africani.",
          "activities": [
            "Tracciamento all'Alba",
            "Picnic nella Savana",
            "Safari Notturno"
          ]
        },
        {
          "day": 9,
          "title": "Progetti di Conservazione e Rinoceronti",
          "description": "Incontra i conservazionisti impegnati nella tutela del rinoceronte e del ghepardo.",
          "activities": [
            "Incontro con gli Elefanti",
            "Centro Rinoceronti",
            "Aperitivo al Tramonto"
          ]
        },
        {
          "day": 10,
          "title": "Passeggiata nel Bush e Cena di Gala Boma",
          "description": "Passeggiata guidata a piedi nella savana e cena finale di gala attorno al fuoco.",
          "activities": [
            "Safari a Piedi",
            "Avvistamento dal Lodge",
            "Gran Banchetto Boma di Saluto"
          ]
        },
        {
          "day": 11,
          "title": "Ultimo Safari all'Alba e Ritorno",
          "description": "Ultimo safari al sorgere del sole seguito da colazione e trasferimento in aeroporto.",
          "activities": [
            "Safari all'Alba",
            "Colazione di Saluto",
            "Trasferimento Aeroporto"
          ]
        }
      ]
    },
    "fr": {
      "title": "L'Expérience Complète – Le Cap, St Lucia, Plage, Safari et Big 5",
      "subtitle": "Le grand voyage sud-africain : beauté du Cap, St Lucia, plages et Big 5",
      "location": "Western Cape et KwaZulu-Natal",
      "country": "Afrique du Sud",
      "badge": "Meilleure Vente",
      "durationText": "Recommandée : 9 Jours Minimum",
      "difficulty": "Relaxed",
      "description": "La grande aventure sud-africaine par excellence. Découvrez Le Cap, la culture Zulu, les croisières safari aux hippopotames à St Lucia, les plages subtropicales de l'océan Indien et les safaris Big 5 d'exception.",
      "highlights": [
        "Explorez Le Cap, Table Mountain et les paysages côtiers",
        "Immersion culturelle authentique dans un village traditionnel Zulu",
        "Détente sur les plages sauvages de l'Océan Indien au KwaZulu-Natal",
        "Safaris Big 5 exceptionnels dans des réserves privées sans paludisme"
      ],
      "accommodations": "Chaque détail est choisi pour une intégration harmonieuse au cœur des écosystèmes. Hôtels d'exception au Cap, lodges de plage et réserves privées.",
      "inclusions": [
        "Hébergements de luxe au Cap, en lodge de plage et en réserve de safari",
        "Tours culturels privés guidés avec les anciens du village Zulu",
        "Tous les safaris en 4x4 ouvert avec pisteurs et guides diplômés",
        "Pension complète gastronomique en safari et tous les permis de conservation"
      ],
      "requiredPreparations": [
        "Bagages pour Tous Environnements : Apportez des vêtements polyvalents, des chaussures confortables, des maillots de bain et une protection solaire pour les différents environnements de votre voyage."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Bienvenue au Cap",
          "description": "Arrivée au Cap et transfert vers votre hôtel de luxe sur le front de mer.",
          "activities": [
            "Accueil VIP",
            "Installation Suite de Luxe",
            "Dîner au Port"
          ]
        },
        {
          "day": 2,
          "title": "Table Mountain et Littoral Atlantique",
          "description": "Téléphérique jusqu'au sommet de Table Mountain puis visite des plages de Camps Bay.",
          "activities": [
            "Sommet Table Mountain",
            "Route Côtière Atlantique",
            "Dîner à Camps Bay"
          ]
        },
        {
          "day": 3,
          "title": "Péninsule du Cap et Manchots de Boulders",
          "description": "Parcourez la péninsule du Cap, visitez la colonie de manchots et le Cap de Bonne-Espérance.",
          "activities": [
            "Chapman’s Peak Drive",
            "Sanctuaire des Manchots",
            "Sentier du Cap de Bonne-Espérance"
          ]
        },
        {
          "day": 4,
          "title": "Vol vers le KwaZulu-Natal et Lodge Océan",
          "description": "Vol intérieur vers la côte subtropicale et installation dans votre hôtel au bord de l'océan.",
          "activities": [
            "Vol Intérieur",
            "Arrivée Hôtel Océan",
            "Thé sur la Terrasse"
          ]
        },
        {
          "day": 5,
          "title": "Immersion Culturelle au Village Zulu",
          "description": "Journée inspirante au cœur d'un village Zulu authentique pour découvrir traditions et artisanat.",
          "activities": [
            "Accueil Traditionnel",
            "Contes des Anciens",
            "Musique et Artisanat Indigenous"
          ]
        },
        {
          "day": 6,
          "title": "Plage Subtropicale et Croisière Hippopotames",
          "description": "Détente à la plage et croisière safari aux hippopotames à St Lucia (UNESCO).",
          "activities": [
            "Marche Côtière",
            "Croisière Hippopotames",
            "Excursion Cape Vidal"
          ]
        },
        {
          "day": 7,
          "title": "Transfert vers la Réserve Privée Big 5",
          "description": "Trajet vers l'intérieur des terres et installation dans votre safari lodge privé.",
          "activities": [
            "Trajet Panoramique",
            "Accueil au Lodge",
            "Safari Big 5 du Soir"
          ]
        },
        {
          "day": 8,
          "title": "Sur la Piste des Big 5 : Lions, Léopards et Éléphants",
          "description": "Safaris matins et soirs avec pisteurs professionnels dans des réserves privées préservées.",
          "activities": [
            "Pistage Matinal",
            "Pique-Nique Savane",
            "Pistage Nocturne"
          ]
        },
        {
          "day": 9,
          "title": "Protection de la Faune et Projets Rhinocéros",
          "description": "Découvrez les programmes éthiques de protection des rhinocéros et félins sauvages.",
          "activities": [
            "Rencontre Éléphants",
            "Centre Rhinocéros",
            "Cocktail au Coucher du Soleil"
          ]
        },
        {
          "day": 10,
          "title": "Marche en Brousse et Grand Banquet Boma",
          "description": "Marche à pied avec rangers puis grand banquet d'adieu autour du feu boma.",
          "activities": [
            "Safari à Pied",
            "Observation au Lodge",
            "Grand Dîner d'Adieu Boma"
          ]
        },
        {
          "day": 11,
          "title": "Dernier Safari Matinal et Vol Retour",
          "description": "Dernier safari au lever du soleil suivi du petit-déjeuner et du transfert aéroport.",
          "activities": [
            "Safari au Lever du Jour",
            "Petit-Déjeuner d'Adieu",
            "Transfert Aéroport"
          ]
        }
      ]
    },
    "de": {
      "title": "Das Gesamterlebnis – Kapstadt, St Lucia, Strand, Safari & Big 5",
      "subtitle": "Die ultimative Südafrika-Reise: Kapstadt, St Lucia, Traumstrände & Big 5",
      "location": "Western Cape und KwaZulu-Natal",
      "country": "Südafrika",
      "badge": "Bestseller",
      "durationText": "Empfohlen: Mindestens 9 Tage",
      "difficulty": "Relaxed",
      "description": "Eine umfassende Traumreise durch die Vielfalt Südafrikas. Erleben Sie Kapstadt, UNESCO-Weltkulturerbe in St Lucia mit Flusssafaris, subtropische Strände und aufregende Big 5 Safaris.",
      "highlights": [
        "Erkunden Sie Kapstadt, den Tafelberg und die wunderschöne Kap-Halbinsel",
        "Authentische Kulturbegegnung in einem traditionellen Zulu-Dorf",
        "Entspannung an den unberührten Stränden des Indischen Ozeans in KwaZulu-Natal",
        "Erstklassige Big 5 Wildbeobachtung in malariafreien privaten Reservaten"
      ],
      "accommodations": "Jedes Detail wird für eine perfekte Integration in die Ökosysteme ausgewählt. Luxus-Hotels in Kapstadt, Strand-Resorts und exklusive Safari-Lodges.",
      "inclusions": [
        "Luxus-Unterkünfte in Kapstadt, an der Küste und in privaten Wildreservaten",
        "Private Kulturführungen mit Dorfältesten der Zulu",
        "Alle Pirschfahrten im offenen Allradfahrzeug mit professionellen Trackern",
        "Vollpension Gourmet-Verpflegung auf Safari und alle Naturschutzgenehmigungen"
      ],
      "requiredPreparations": [
        "Vielseitig Packen: Bringen Sie vielseitige Kleidung, bequemes Schuhwerk, Bademode und Sonnenschutz für die verschiedenen Umgebungen Ihrer Reise mit."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Willkommen in Kapstadt",
          "description": "Ankunft in Kapstadt und Transfer zu Ihrem Waterfront-Luxushotel.",
          "activities": [
            "VIP-Empfang",
            "Check-in Luxussuite",
            "Abendessen an der Waterfront"
          ]
        },
        {
          "day": 2,
          "title": "Tafelberg & Atlantikküste",
          "description": "Auffahrt auf den Tafelberg und Fahrt entlang der malerischen Küste von Camps Bay.",
          "activities": [
            "Tafelberg-Gipfel",
            "Atlantik-Küstenfahrt",
            "Abendessen in Camps Bay"
          ]
        },
        {
          "day": 3,
          "title": "Kap-Halbinsel & Pinguine am Boulders Beach",
          "description": "Fahrt zum Kap der Guten Hoffnung und Besuch der afrikanischen Pinguine.",
          "activities": [
            "Chapman’s Peak Drive",
            "Pinguin-Schutzgebiet",
            "Wanderung am Kap der Guten Hoffnung"
          ]
        },
        {
          "day": 4,
          "title": "Flug nach KwaZulu-Natal & Strand-Hotel",
          "description": "Flug an die subtropische Küste und Check-in in Ihrem Resort am Indischen Ozean.",
          "activities": [
            "Inlandsflug-Transfer",
            "Ankunft Ozean-Resort",
            "Nachmittagstee auf der Terrasse"
          ]
        },
        {
          "day": 5,
          "title": "Authentische Zulu-Kultur im Dorf",
          "description": "Ein inspirierender Tag im Zulu-Dorf zum Kennenlernen von Traditionen, Handwerk und Musik.",
          "activities": [
            "Traditioneller Empfang",
            "Geschichten der Ältesten",
            "Handwerk & Musik-Führung"
          ]
        },
        {
          "day": 6,
          "title": "Küsten-Entspannung & Nilpferd-Flusssafari",
          "description": "Entspannter Tag am Strand und Bootsfahrt zu den Nilpferden in St Lucia (UNESCO).",
          "activities": [
            "Küstenspaziergang",
            "Nilpferd-Bootssafari",
            "Ausflug Cape Vidal"
          ]
        },
        {
          "day": 7,
          "title": "Fahrt ins Big 5 Wildreservat",
          "description": "Fahrt durch die grünen Hügel des Zululands zu Ihrer exklusiven Safari-Lodge.",
          "activities": [
            "Panoramafahrt",
            "Empfang in der Lodge",
            "Erste Abend-Pirschfahrt"
          ]
        },
        {
          "day": 8,
          "title": "Big 5 Spurensuche: Löwen, Leoparden, Elefanten",
          "description": "Begeben Sie sich mit Fährtenlesern auf die Suche nach den afrikanischen Big 5.",
          "activities": [
            "Frühmorgens-Spurensuche",
            "Savannen-Picknick",
            "Nacht-Pirschfahrt"
          ]
        },
        {
          "day": 9,
          "title": "Artenschutz & Nashorn-Projekte",
          "description": "Erfahren Sie mehr über den Schutz bedrohter Arten wie Nashörner und Geparde.",
          "activities": [
            "Elefanten-Begegnung",
            "Nashorn-Schutzzentrum",
            "Sonnenuntergangs-Drinks"
          ]
        },
        {
          "day": 10,
          "title": "Buschwanderung & Festliches Boma-Abendessen",
          "description": "Wanderung zu Fuß durch die Savanne mit Rangern, gefolgt von einem festlichen Boma-Festmahl.",
          "activities": [
            "Fuß-Safari",
            "Wildbeobachtung",
            "Festliches Boma-Abschiedsessen"
          ]
        },
        {
          "day": 11,
          "title": "Letzte Morgen-Pirschfahrt & Heimreise",
          "description": "Eine unvergessliche letzte Pirschfahrt bei Sonnenaufgang vor dem Frühstück und Flughafentransfer.",
          "activities": [
            "Sonnenaufgangs-Pirschfahrt",
            "Abschiedsfrühstück",
            "Flughafentransfer"
          ]
        }
      ]
    }
  },
  "ocean-expeditions-private-beach-lodge": {
    "it": {
      "title": "Spedizioni Oceaniche e Private Beach Lodge",
      "subtitle": "Esclusive avventure marine, santuari di nidificazione delle tartarughe e lodge sulla spiaggia",
      "location": "KwaZulu-Natal",
      "country": "Sudafrica",
      "durationText": "Consigliata: 6 Giorni",
      "difficulty": "Relaxed",
      "description": "Fuga in un paradiso incontaminato dove antiche foreste dunali incontrano le acque turchesi dell'Oceano Indiano nel Parco iSimangaliso (UNESCO). Goditi il lusso riservato di un lodge sulla spiaggia, snorkeling guidato tra barriere coralline e tracciamento notturno delle tartarughe.",
      "highlights": [
        "Soggiorno in un eco-lodge di lusso nascosto dietro le dune costiere",
        "Snorkeling e immersioni subacquee in barriere coralline protette",
        "Tracciamento notturno stagionale delle tartarughe liuto e caretta (Nov–Feb)",
        "Safari oceanici in barca, crociere al tramonto sul lago e trekking nella foresta"
      ],
      "accommodations": "Ogni dettaglio è scelto per la massima integrazione negli ecosistemi costieri. Bungalow in paglia di lusso vista oceano, cucina di mare e relax.",
      "inclusions": [
        "Soggiorno in eco-lodge di lusso sulla spiaggia con vista oceano",
        "Tutti i pasti giornalieri, picnic in spiaggia e grigliate serali sul mare",
        "Escursioni marine guidate, attrezzatura da snorkeling e passeggiate nella foresta",
        "Tasse di conservazione del parco e del santuario marino"
      ],
      "requiredPreparations": [
        "Essenziali per l'Oceano: Porta costumi da bagno, protezione solare e abbigliamento adatto per le attività in spiaggia e nell'oceano."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Arrivo nella Natura Incontaminata della Costa",
          "description": "Arrivo tra le dune della foresta costiera fino al vostro eco-lodge riservato sulla spiaggia.",
          "activities": [
            "Check-in tra le Dune",
            "Passeggiata al Tramonto",
            "Cena di Pesce Fresco"
          ]
        },
        {
          "day": 2,
          "title": "Snorkeling nella Barriera Corallina & Safari Oceanico",
          "description": "Uscita in barca nell'Oceano Indiano per esplorare le barriere coralline con pesci tropicali e tartarughe.",
          "activities": [
            "Snorkeling Guidato",
            "Safari Oceanico in Barca",
            "Pranzo a Base di Pesce"
          ]
        },
        {
          "day": 3,
          "title": "Passeggiate nella Foresta & Crociera al Tramonto",
          "description": "Scopri la ricca avifauna della foresta costiera e fai una crociera rilassante sull'estuario.",
          "activities": [
            "Birdwatching nella Foresta",
            "Avvistamento Ippopotami",
            "Aperitivo sul Lago"
          ]
        },
        {
          "day": 4,
          "title": "Nidificazione delle Tartarughe & Osservazione Stelle",
          "description": "Passeggiata guidata notturna sulla spiaggia per osservare le tartarughe deporre le uova (Nov–Feb).",
          "activities": [
            "Tracciamento Tartarughe",
            "Stelle sulla Spiaggia",
            "Cioccolata Calda al Fuoco"
          ]
        },
        {
          "day": 5,
          "title": "Safari nel Parco Umido & Immersione Subacquea",
          "description": "Tempo libero in spiaggia, safari in veicolo aperto nel parco umido o immersioni subacquee.",
          "activities": [
            "Sessione Sub / Snorkeling",
            "Safari nel Parco iSimangaliso",
            "Cena Grigliata sulla Spiaggia"
          ]
        },
        {
          "day": 6,
          "title": "Alba sull'Oceano & Partenza",
          "description": "Alba spettacolare sull'Oceano Indiano prima della colazione tropicale e del trasferimento.",
          "activities": [
            "Passeggiata all'Alba",
            "Colazione al Lodge",
            "Trasferimento Partenza"
          ]
        }
      ]
    },
    "fr": {
      "title": "Expéditions Océaniques et Lodge de Plage Privé",
      "subtitle": "Aventures marines exclusives, sanctuaire de tortues marines et lodge de plage confidentiel",
      "location": "KwaZulu-Natal",
      "country": "Afrique du Sud",
      "durationText": "Recommandée : 6 Jours",
      "difficulty": "Relaxed",
      "description": "Évadez-vous dans un paradis préservé où les forêts de dunes s'allient aux eaux turquoises de l'Océan Indien dans le parc d'iSimangaliso (UNESCO). Profitez du confort d'un lodge de plage exclusif, de safaris marins et du pistage nocturne des tortues.",
      "highlights": [
        "Séjour dans un éco-lodge de luxe niché derrière les dunes côtières",
        "Snorkeling et plongée sous-marine sur des récifs coralliens protégés",
        "Pistage nocturne saisonnier des tortues marines (Luth et Caouanne)",
        "Safaris en bateau oceanique, croisières au coucher du soleil et marches en forêt"
      ],
      "accommodations": "Chaque détail est choisi pour une intégration harmonieuse au cœur des écosystèmes. Bungalows de luxe en toit de chaume vue mer et repas gastronomiques.",
      "inclusions": [
        "Hébergement en lodge de plage en toit de chaume avec vue mer",
        "Tous les repas quotidiens, pique-niques sur la plage et barbecues en bord de mer",
        "Excursions marines guidées, équipement de snorkeling et marches en forêt",
        "Taxes de conservation du parc et du sanctuaire marin"
      ],
      "requiredPreparations": [
        "Essentiels Océan : Apportez des maillots de bain, une protection solaire et des vêtements adaptés aux activités de plage et d'océan."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Arrivée dans la Réserve Côtière Préservée",
          "description": "Traversée des forêts de dunes pour rejoindre votre éco-lodge intime en bord de mer.",
          "activities": [
            "Check-in Forêt de Dunes",
            "Balade au Coucher du Soleil",
            "Dîner Produits de la Mer"
          ]
        },
        {
          "day": 2,
          "title": "Snorkeling Récif Corallien & Safari Marin",
          "description": "Embarquez dans l'Océan Indien pour explorer les récifs coralliens et rencontrer raie manta et tortues.",
          "activities": [
            "Snorkeling Guidé",
            "Safari Bateau en Mer",
            "Déjeuner de Poisson"
          ]
        },
        {
          "day": 3,
          "title": "Forêt de Dunes & Croisière au Coucher du Soleil",
          "description": "Découvrez la faune aviaire de la forêt de dunes suivie d'une croisière paisible sur le lac.",
          "activities": [
            "Marche Ornithologique",
            "Observation Hippopotames",
            "Cocktail au Bord du Lac"
          ]
        },
        {
          "day": 4,
          "title": "Pistage des Tortues Marines & Nuit Étoilée",
          "description": "Marche nocturne guidée le long des plages pour observer la ponte des tortues géantes (Nov–Fév).",
          "activities": [
            "Pistage des Tortues",
            "Observation des Étoiles",
            "Chocolat Chaud au Coin du Feu"
          ]
        },
        {
          "day": 5,
          "title": "Safari Parc Humide & Plongée Sous-Marine",
          "description": "Détente sur des plages désertes, safari en véhicule ouvert dans le parc ou plongée sous-marine.",
          "activities": [
            "Session Plongée / Snorkeling",
            "Safari Parc iSimangaliso",
            "Dîner Barbecue sur la Plage"
          ]
        },
        {
          "day": 6,
          "title": "Lever de Soleil Océanique & Départ",
          "description": "Admirez un lever de soleil magnifique sur l'Océan Indien avant le petit-déjeuner et votre transfert.",
          "activities": [
            "Marche Matinale au Lever du Jour",
            "Petit-Déjeuner du Lodge",
            "Transfert Départ"
          ]
        }
      ]
    },
    "de": {
      "title": "Ozean-Expeditionen & Private Strand-Lodge",
      "subtitle": "Exklusive Meeresabenteuer, Schildkröten-Schutzgebiete & einsame Luxus-Strandlodge",
      "location": "KwaZulu-Natal",
      "country": "Südafrika",
      "durationText": "Empfohlen: 6 Tage",
      "difficulty": "Relaxed",
      "description": "Entfliehen Sie in ein unberührtes Paradies, wo Küstenwälder auf das türkisfarbene Wasser des Indischen Ozeans im iSimangaliso Wetland Park (UNESCO) treffen. Genießen Sie den Luxus einer abgelegenen Strandlodge und Meeres-Safaris.",
      "highlights": [
        "Aufenthalt in einer abgelegenen Öko-Luxus Strandlodge hinter Küstendünen",
        "Schnorcheln und Tauchen an geschützten Korallenriffen von Weltklasse",
        "Saisonale nächtliche Schildkröten-Spurensuche (Lederschildkröten & Unechte Karettschildkröten)",
        "Ozean-Bootssafaris, Sonnenuntergangs-Bootstouren & Küstenwald-Wanderungen"
      ],
      "accommodations": "Jedes Detail wird für eine perfekte Integration in die Küstenökosysteme ausgewählt. Reetgedeckte Luxus-Bungalows mit Meerblick und Meeresfrüchte-Gourmetküche.",
      "inclusions": [
        "Unterkunft in reetgedeckter Luxus-Strandlodge mit Meerblick",
        "Alle täglichen Mahlzeiten, Strandpicknicks und abendliche Braai-Abendessen am Strand",
        "Geführte Meeresausflüge, Schnorchelausrüstung und Waldwanderungen",
        "Park- und Meresschutzgebiets-Abgaben"
      ],
      "requiredPreparations": [
        "Ozean-Essentials: Bringen Sie Bademode, Sonnenschutz und geeignete Kleidung für Strand- und Ozeanaktivitäten mit."
      ],
      "detailedItinerary": [
        {
          "day": 1,
          "title": "Ankunft im Unberührten Küsten-Wildschutzgebiet",
          "description": "Fahrt durch die Dünenwälder zu Ihrer abgeschiedenen Öko-Luxus-Strandlodge.",
          "activities": [
            "Check-in im Dünenwald",
            "Strandspaziergang im Abendlicht",
            "Frisches Fisch-Abendessen"
          ]
        },
        {
          "day": 2,
          "title": "Korallenriff-Schnorcheln & Ozean-Safari",
          "description": "Bootsausflug auf den Indischen Ozean zur Erkundung geschützter Riffe mit Schildkröten und Rochen.",
          "activities": [
            "Geführtes Riffschnorcheln",
            "Meeres-Bootssafari",
            "Meeresfrüchte-Mittagessen"
          ]
        },
        {
          "day": 3,
          "title": "Küstenwald-Wanderung & Bootsfahrt",
          "description": "Entdecken Sie die Vogelwelt der Küstendünenwälder, gefolgt von einer ruhigen Bootsfahrt auf dem See.",
          "activities": [
            "Vogelbeobachtung im Wald",
            "Nilpferd-Beobachtung",
            "Sonnenuntergang am See"
          ]
        },
        {
          "day": 4,
          "title": "Schildkröten-Nistbeobachtung & Sternenhimmel",
          "description": "Magische geführte Nachtwanderung am Strand zur Beobachtung der Schildkröteneiablage (Nov–Feb).",
          "activities": [
            "Schildkröten-Tracking",
            "Sternenbeobachtung am Strand",
            "Heiße Schokolade am Lagerfeuer"
          ]
        },
        {
          "day": 5,
          "title": "Wetland Park Safari & Tauchgang",
          "description": "Freizeit an einsamen Stränden, Safari im offenen Geländewagen im Wetland Park oder Gerätetauchen.",
          "activities": [
            "Tauch- / Schnorcheleinheit",
            "iSimangaliso Park Safari",
            "Strand-Braai-Abendessen"
          ]
        },
        {
          "day": 6,
          "title": "Sonnenaufgang am Ozean & Abreise",
          "description": "Erleben Sie einen atemberaubenden Sonnenaufgang über dem Indischen Ozean vor dem Frühstück und Transfer.",
          "activities": [
            "Sonnenaufgangsspaziergang",
            "Abschiedsfrühstück",
            "Abreise-Transfer"
          ]
        }
      ]
    }
  }
};

	return expeditions.map(exp => {
		const langData = translations[exp.id]?.[currentLanguage];
		if (langData) {
			return {
				...exp,
				...langData,
			};
		}
		return exp;
	});
};
